import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { hasEmail, isEmptyObject, showNotificationDialog } from '../utils';
import useR05Api from '../api/useR05Api';
import FujiModal from '../components/fuji-modal/FujiModal';
import { useFujiAuth } from '../auth/useFujiAuth';
import { localStorageKeys, PAYPAY_NATIVE_WEBVIEW_CONFIG, TIMEOUT_DURATION } from '../utils/Constants';
import { ErrorType, PurchaseResponse, SelectNumber } from 'types';
import {
    getInjectedObjectFromNativeApp,
    isReactNativeWebView,
    sendMessageToNativeApp,
} from '../utils/reactNativeUtils';

/**
 * チケット購入処理のカスタムフック
 */
const usePurchaseTicket = () => {
    const { i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [warningMessenger, setWarningMessenger] = useState<string>('');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        purchaseTicket,
        purchaseTicketWithPayPay,
        purchaseTicketWithCard,
        purchaseTicketWithCardComplete,
        purchaseTicketWithNoCost,
        distributionsTicket,
        reservationUpdate,
        applePayAuth,
    } = useR05Api();

    const { user } = useFujiAuth();

    /**
     * チケットの購入に失敗した時のトーストメッセージを表示する
     */
    const showPurchaseTicketError = useCallback(
        (error: ErrorType) => {
            const showError = (text: string) => {
                showNotificationDialog('error', 'buyTicket', t('notification.title'), text);
            };
            if (error.isTimeout) {
                showError(t('error-message.server-error'));
                return;
            }
            switch (error.errorType) {
                case 'offline':
                    showError(t('error-message.server-error'));
                    break;
                case 'out_of_sale_ticket':
                case 'reservation_not_started':
                    showError(t('error-message.buy-ticket.out-of-date'));
                    break;
                case 'undefined_parameter':
                case 'invalid_parameter':
                case 'not_found_ticket':
                case 'gmo_error':
                case 'out_of_date_token':
                case 'invalid_signature_token':
                case 'invalid_api_key':
                case 'processing_failures':
                    showError(t('error-message.process-error'));
                    break;
                case 'insufficient_balance':
                    showError(t('error-message.buy-ticket.insufficient-balance'));
                    break;
                case 'amount_limit_exceeded':
                    showError(t('error-message.buy-ticket.amount-limit-exceeded'));
                    break;
                case 'invalid_cvc':
                    showError(t('error-message.buy-ticket.invalid-cvc'));
                    break;
                case 'invalid_contract':
                    showError(t('error-message.buy-ticket.invalid-contract'));
                    break;
                case 'processing_failure_invalid_card_parameter':
                    showError(t('error-message.buy-ticket.invalid-card-parameter'));
                    break;
                case 'processing_failure_tds_failed':
                    showError(t('error-message.buy-ticket.tds-failed'));
                    break;
                case 'gmo_settlement_error':
                case 'processing_failure_others':
                case 'issuer_settlement_error':
                    showError(t('error-message.buy-ticket.settlement-error'));
                    break;
                case 'card_declined':
                    showError(t('error-message.buy-ticket.card-declined'));
                    break;
                case 'settlement_aborted':
                    showError(t('error-message.buy-ticket.settlement-aborted'));
                    break;
                case 'not_enough_stock':
                case 'not_enough_sale_ticket_count':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.buy-ticket.not-enough-stock') ?? '');
                    break;
                case 'reservation_ended':
                    showError(t('error-message.buy-ticket.reservation-ended'));
                    break;
                case 'reservation_reception_suspended':
                case 'cannot_reserved_at_enable_refunds':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.buy-ticket.reservation-reception-suspended') ?? '');
                    break;
                case 'unmatched_price':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.reservation.unmatched-price') ?? '');
                    break;
                case 'already_used_voucher_code':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.voucher-code.already-used') ?? '');
                    break;
                case 'processing_voucher_code':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.voucher-code.processing') ?? '');
                    break;
                case 'disallowed_cancel_policies':
                    setIsWarningModalOpen(true);
                    setWarningMessenger(t('error-message.reservation.disallowed-cancel-policies') ?? '');
                    break;
                case 'unexpected':
                case 'unexpected_error':
                default:
                    showError(t('error-message.tickets.unexpected-error'));
                    break;
            }
        },
        [t],
    );

    const showOfflineError = useCallback(
        () => showPurchaseTicketError({ errorType: 'offline' }),
        [showPurchaseTicketError],
    );

    /**
     * チケット購入完了後の処理
     */
    const afterPurchaseSuccess = useCallback(
        (response: PurchaseResponse) => {
            if (response == null) {
                return;
            }
            // チケット購入完了画面へ遷移
            navigate('/buy/ticket/success', { state: response });
        },
        [navigate],
    );

    /**
     * チケット購入処理
     */
    const purchase = useCallback(
        async ({ salesTicketId, number }: { salesTicketId: number; number: SelectNumber }) => {
            try {
                setIsLoading(true);
                const request = {
                    salesTicketId: salesTicketId,
                    lang: i18n.language,
                    countAdult: number?.adult ?? 0,
                    countCustom1: number?.custom1 ?? 0,
                    countCustom2: number?.custom2 ?? 0,
                    countCustom3: number?.custom3 ?? 0,
                };

                const response = await purchaseTicket(request);
                afterPurchaseSuccess(response);
            } catch (error: any) {
                showPurchaseTicketError(error);
            } finally {
                setIsLoading(false);
            }
        },
        [afterPurchaseSuccess, i18n.language, purchaseTicket, showPurchaseTicketError],
    );

    /**
     * PayPayでチケット購入
     */
    const purchaseWithPayPay = useCallback(
        async ({
            salesTicketId,
            number,
            paymentType,
            reservationCombo,
            userForm,
            anonymousFormAnswers,
        }: {
            salesTicketId?: number;
            number: SelectNumber;
            paymentType: string;
            reservationCombo?: any;
            userForm: any;
            anonymousFormAnswers: any;
        }) => {
            try {
                if (!navigator.onLine) {
                    showOfflineError();
                    return;
                }
                setIsLoading(true);
                const phoneNumberFormat = `${userForm?.countryCode?.dialCode}-${userForm?.phoneNumber}`;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { countryCode, confirm, ...rest } = userForm ?? {};
                const { voucherCode, ...anonymousFormRequest } = anonymousFormAnswers ?? {};
                const userFormRequest = userForm
                    ? {
                          ...rest,
                          phoneNumber: phoneNumberFormat,
                      }
                    : undefined;
                const isNativeWebView = isReactNativeWebView();
                const nativeObject = isNativeWebView
                    ? (getInjectedObjectFromNativeApp() as { bundleIdentifier: string } | null)
                    : null;
                const isFnAppWebView =
                    nativeObject?.bundleIdentifier === PAYPAY_NATIVE_WEBVIEW_CONFIG.fnAppBundleIdentifier;
                const getCallbackUrl = () => {
                    if (isFnAppWebView) {
                        return PAYPAY_NATIVE_WEBVIEW_CONFIG.fnAppCallbackUrl;
                    }
                    return window.location.origin + '/buy/ticket/credit-certificate';
                };
                const callbackUrl = getCallbackUrl();
                const request = {
                    salesTicketId: salesTicketId,
                    lang: i18n.language,
                    callbackUrl: callbackUrl,
                    paymentType: paymentType,
                    numberAdult: number?.adult ?? 0,
                    numberCustom1: number?.custom1 ?? 0,
                    numberCustom2: number?.custom2 ?? 0,
                    numberCustom3: number?.custom3 ?? 0,
                    reservationCombo,
                    userForm: userFormRequest,
                    anonymousFormAnswers: isEmptyObject(anonymousFormRequest) ? null : anonymousFormRequest,
                    voucherCode: voucherCode ?? null,
                };

                const response = await purchaseTicketWithPayPay(request);
                if (response != null && response.redirectUrl && response.nextAction === 'REDIRECT') {
                    if (isFnAppWebView) {
                        const sentToNative = sendMessageToNativeApp('paypay_redirect', {
                            url: response.redirectUrl,
                            orderId: response.orderId,
                        });
                        if (!sentToNative) {
                            window.open(response.redirectUrl, '_self');
                        }
                        return;
                    }
                    window.open(response.redirectUrl, '_self');
                } else {
                    afterPurchaseSuccess(response);
                }
            } catch (error: any) {
                showPurchaseTicketError(error);
            } finally {
                setIsLoading(false);
            }
        },
        [afterPurchaseSuccess, i18n.language, purchaseTicketWithPayPay, showOfflineError, showPurchaseTicketError],
    );

    /**
     * クレジットカードでチケット購入
     */
    const purchaseWithCard = useCallback(
        async ({
            salesTicketId,
            number,
            cardToken,
            paymentType,
            reservationCombo,
            userForm,
            email,
            anonymousFormAnswers,
        }: {
            salesTicketId?: number;
            number?: SelectNumber | null;
            paymentType: string;
            cardToken?: string;
            email?: string | null;
            reservationCombo?: Array<any>;
            userForm: any;
            anonymousFormAnswers: any;
        }) => {
            try {
                setIsLoading(true);
                const phoneNumberFormat = `${userForm?.countryCode?.dialCode}-${userForm?.phoneNumber}`;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { countryCode, confirm, ...rest } = userForm ?? {};
                const { voucherCode, ...anonymousFormRequest } = anonymousFormAnswers ?? {};
                const userFormRequest = userForm
                    ? {
                          ...rest,
                          phoneNumber: phoneNumberFormat,
                      }
                    : undefined;

                const request = {
                    salesTicketId: salesTicketId,
                    lang: i18n.language,
                    callbackUrl: window.location.origin + '/buy/ticket/credit-certificate',
                    cardToken: cardToken,
                    paymentType: paymentType,
                    numberAdult: number?.adult ?? 0,
                    numberCustom1: number?.custom1 ?? 0,
                    numberCustom2: number?.custom2 ?? 0,
                    numberCustom3: number?.custom3 ?? 0,
                    reservationCombo,
                    userForm: userFormRequest,
                    email: email,
                    anonymousFormAnswers: isEmptyObject(anonymousFormRequest) ? null : anonymousFormRequest,
                    voucherCode: voucherCode ?? null,
                };

                const response = await purchaseTicketWithCard(request);
                if (response != null && response.redirectUrl && response.nextAction === 'REDIRECT') {
                    window.open(response.redirectUrl, '_self');
                } else {
                    afterPurchaseSuccess(response);
                }
                return true;
            } catch (error: any) {
                showPurchaseTicketError(error);
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [afterPurchaseSuccess, i18n.language, purchaseTicketWithCard, showPurchaseTicketError],
    );

    const completePurchase = useCallback(
        async (p: string) => {
            try {
                setIsLoading(true);
                const { accessId, csrfToken } = JSON.parse(decodeURIComponent(atob(p)));
                const result = await purchaseTicketWithCardComplete({
                    accessId: accessId,
                    transactionId: csrfToken,
                });
                return {
                    orderId: result,
                };
            } catch (error: any) {
                console.error(error);
                showPurchaseTicketError(error);
                return {
                    orderId: null,
                };
            } finally {
                setIsLoading(false);
            }
        },
        [purchaseTicketWithCardComplete, showPurchaseTicketError],
    );

    const hasEmailInUserInfo = useCallback(() => {
        if (user && !hasEmail(user)) {
            setIsEmailModalOpen(true);
            return false;
        }
        return true;
    }, [user]);

    const WarningConfirmModal = useCallback<() => JSX.Element>(() => {
        return (
            <FujiModal
                isOpen={isWarningModalOpen}
                onSubmit={() => {
                    setIsWarningModalOpen(false);
                }}
                txtSubmit={t('modal.warning-confirm')}
                body={warningMessenger}
            />
        );
    }, [isWarningModalOpen, t]);

    const EmailConfirmModal = useCallback<() => JSX.Element>(() => {
        return (
            <FujiModal
                isOpen={isEmailModalOpen}
                onSubmit={() => {
                    navigate('/profile/info/change-mail', { state: { isInPurchase: true } });
                }}
                onCancel={() => {
                    setIsEmailModalOpen(false);
                }}
                txtCancel={t('button.close') ?? ''}
                txtSubmit={t('modal.email-change')}
                body={t('modal.invalid-email')}
            />
        );
    }, [isEmailModalOpen, t, navigate]);

    const purchaseWithNoCost = useCallback(
        async ({
            salesTicketId,
            number,
            anonymousFormAnswers,
            userForm,
            reservationCombo,
            email,
        }: {
            salesTicketId?: number;
            number: SelectNumber;
            reservationCombo?: any;
            userForm: any;
            anonymousFormAnswers: any;
            email?: string;
        }) => {
            try {
                if (!navigator.onLine) {
                    showOfflineError();
                    return;
                }
                setIsLoading(true);
                const phoneNumberFormat = `${userForm?.countryCode?.dialCode}-${userForm?.phoneNumber}`;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { countryCode, confirm, ...rest } = userForm ?? {};
                const { voucherCode, ...anonymousFormRequest } = anonymousFormAnswers ?? {};
                const userFormRequest = userForm
                    ? {
                          ...rest,
                          phoneNumber: phoneNumberFormat,
                      }
                    : undefined;

                const request = {
                    salesTicketId: salesTicketId,
                    lang: i18n.language,
                    numberAdult: number?.adult ?? 0,
                    numberCustom1: number?.custom1 ?? 0,
                    numberCustom2: number?.custom2 ?? 0,
                    numberCustom3: number?.custom3 ?? 0,
                    userForm: userFormRequest,
                    reservationCombo,
                    email,
                    anonymousFormAnswers: isEmptyObject(anonymousFormRequest) ? null : anonymousFormRequest,
                    voucherCode: voucherCode ?? null,
                };

                const response = await purchaseTicketWithNoCost(request);
                if (response == null || response.result !== 'succeeded') {
                    throw new Error('Failed');
                }
                afterPurchaseSuccess(response);
            } catch (error: any) {
                showPurchaseTicketError(error);
            } finally {
                setIsLoading(false);
            }
        },
        [afterPurchaseSuccess, i18n.language, purchaseTicketWithNoCost, showOfflineError, showPurchaseTicketError],
    );

    const distributionsOrderTicket = useCallback(
        async ({ orderTicketId, distributeCount }: { orderTicketId: string; distributeCount: SelectNumber }) => {
            try {
                setIsLoading(true);

                const params = Object.entries(distributeCount)
                    .map(([key, value]) => ({
                        orderTicketId,
                        distributeNumber: value,
                        distributeType: key,
                    }))
                    .filter(({ distributeNumber }) => (distributeNumber ?? 0) > 0);

                const [response] =
                    (await Promise.all(params.map((param) => distributionsTicket(param, TIMEOUT_DURATION)))) ?? [];

                return response?.orderTicketId;
            } catch (error: any) {
                showPurchaseTicketError(error);
            } finally {
                setIsLoading(false);
            }
        },
        [],
    );

    /**
     * 予約チケット変更
     */
    const reservationTicketUpdate = useCallback(
        async ({ userForm, ...requestParams }: any) => {
            try {
                if (!navigator.onLine) {
                    showOfflineError();
                    return;
                }
                setIsLoading(true);
                const phoneNumberFormat = `${userForm?.countryCode?.dialCode}-${userForm?.phoneNumber}`;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { countryCode, confirm, ...rest } = userForm ?? {};
                const userFormRequest = userForm
                    ? {
                          ...rest,
                          phoneNumber: phoneNumberFormat,
                      }
                    : undefined;
                const request = {
                    ...requestParams,
                    userForm: userFormRequest,
                };

                const response = await reservationUpdate(request);
                showNotificationDialog('success', '', t('notification.title'), t('notification.update-succeed'));
                localStorage.removeItem(localStorageKeys.TICKET_INFO_DATA);
                localStorage.removeItem(localStorageKeys.RESERVATION_INFO_DATA);
                localStorage.removeItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS);
                localStorage.removeItem(localStorageKeys.RESERVATION_FLOW);
                return response;
            } catch (error: any) {
                showPurchaseTicketError(error);
            } finally {
                setIsLoading(false);
            }
        },
        [reservationUpdate, showOfflineError, showPurchaseTicketError],
    );

    const applePayPaymentSession = useCallback(
        async (params: {
            secretName: string;
            validationUrl: string;
            displayName: string;
            merchantIdentifier: string;
            initiativeContext: string;
        }) => {
            try {
                if (!navigator.onLine) {
                    showOfflineError();
                    return;
                }
                setIsLoading(true);
                return await applePayAuth(params);
            } catch (error: any) {
                showPurchaseTicketError(error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [afterPurchaseSuccess, i18n.language, purchaseTicketWithNoCost, showOfflineError, showPurchaseTicketError],
    );

    return {
        isLoading,
        purchaseTicket: purchase,
        purchaseWithPayPay,
        purchaseWithCard,
        purchaseWithNoCost,
        completePurchase,
        showOfflineError,
        hasEmailInUserInfo,
        WarningConfirmModal,
        EmailConfirmModal,
        distributionsOrderTicket,
        reservationTicketUpdate,
        applePayPaymentSession,
    };
};

export default usePurchaseTicket;
